import React, { useMemo } from 'react';
import { View, Pressable, Image, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import ToolAvatar from '@/components/Tools/ToolAvatar';
import {
  OUTPUT_TYPE_ICON,
  formatTimeAgo,
  getFileUrl,
  getPaletteForTool,
  getRunStatusVisual,
  summarizeRunInputs,
  toolRunResultPreviewSnippet,
  resolveToolMediaPreviewAspectRatio,
} from '@/components/Tools/toolDetailHelpers';

/**
 * Rich, grid-friendly card for a single ToolRun on the history page.
 *
 * Visual layout (top → bottom):
 *   1. Media thumbnail: real image / video frame / text snippet, with a
 *      bottom-anchored status chip and a hover-only delete button.
 *   2. Tool identity row: small category-tinted icon, tool name, time-ago.
 *   3. Footer: action button ("Open in tool") + credit cost.
 *
 * Tapping the card or the action button deep-links back to the originating
 * tool with `?runId=<id>` so the run can be inspected, tweaked, and re-run.
 */
function ToolHistoryCard({
  run,
  isDeleting = false,
  onOpen,
  onDelete,
}) {
  const { colors } = useTheme();
  const { t } = useTranslation();

  const tool      = run?.tool || null;
  const palette   = useMemo(() => getPaletteForTool(tool), [tool]);
  const status    = useMemo(() => getRunStatusVisual(run?.status, colors, palette, t), [run?.status, colors, palette, t]);

  const firstFile = Array.isArray(run?.resultFiles) ? run.resultFiles[0] : null;
  const fileUrl   = getFileUrl(firstFile);
  const mime      = firstFile?.mimeType || '';
  const isImage   = mime.startsWith('image/');
  const isVideo   = mime.startsWith('video/');
  const isAudio   = mime.startsWith('audio/');
  // Posters / thumbnails for non-pixel media (3D models, documents). The
  // serialized File model exposes this as `thumbnailPath` (already an
  // absolute CDN URL); the other keys are tolerated fallbacks.
  const posterUrl = !isImage && !isVideo
    ? (firstFile?.thumbnailPath || firstFile?.thumbnailUrl || firstFile?.previewUrl || null)
    : null;
  // For non-media output, fall back to either the produced text OR the
  // user's actual inputs (e.g. "golden flower · glb"). The compiled prompt
  // is intentionally NOT used here — it's the template-substituted system
  // payload and tends to be identical across runs of the same tool.
  const inputSummary = useMemo(() => summarizeRunInputs(run, 140), [run]);
  const previewText = toolRunResultPreviewSnippet(run, 140) || inputSummary || '';
  const previewAspect = useMemo(
    () => ((isImage || isVideo) ? resolveToolMediaPreviewAspectRatio(firstFile, run, tool?.outputType) : 16 / 10),
    [firstFile, run, tool?.outputType, isImage, isVideo],
  );

  const styles = useMemo(() => StyleSheet.create({
    card: {
      width: '100%',
      borderRadius: 16,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      overflow: 'hidden',
      ...(Platform.OS === 'web' ? {
        cursor: 'pointer',
        transition: 'border-color 0.15s ease',
      } : null),
    },
    cardHover: {
      borderColor: palette.fg,
    },
    thumb: {
      width: '100%',
      backgroundColor: colors.solid_color_tertiary,
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      position: 'relative',
    },
    thumbMedia: { width: '100%', height: '100%' },
    thumbAccent: {
      position: 'absolute',
      inset: 0,
      backgroundColor: palette.soft,
    },
    scrim: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 70,
    },
    textPreview: {
      padding: 16,
      flex: 1,
      width: '100%',
      flexDirection: 'row',
      gap: 10,
    },
    textPreviewMark: {
      width: 3,
      borderRadius: 2,
      backgroundColor: palette.fg,
      opacity: 0.7,
    },
    statusChip: {
      position: 'absolute',
      bottom: 10,
      left: 10,
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      paddingVertical: 4,
      paddingHorizontal: 9,
      borderRadius: 999,
      backgroundColor: 'rgba(0, 0, 0, 0.65)',
      ...(Platform.OS === 'web' ? { backdropFilter: 'blur(4px)' } : null),
    },
    deleteBtn: {
      position: 'absolute',
      top: 8,
      right: 8,
      width: 26,
      height: 26,
      borderRadius: 13,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.55)',
      ...(Platform.OS === 'web' ? {
        backdropFilter: 'blur(4px)',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
      } : null),
    },
    deleteBtnHover: {
      backgroundColor: 'rgba(220, 38, 38, 0.92)',
    },
    body: {
      padding: 14,
      gap: 10,
    },
    toolRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 9,
    },
    toolIconWrap: {
      width: 28,
      height: 28,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: palette.bg,
      flexShrink: 0,
    },
    toolMeta: { flex: 1, minWidth: 0 },
    actionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderTopWidth: 1,
      borderTopColor: colors.border_color_secondary,
      paddingTop: 10,
      marginTop: 2,
    },
    actionBtn: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 6,
      paddingHorizontal: 10,
      borderRadius: 8,
      backgroundColor: palette.bg,
    },
    actionBtnHover: {
      backgroundColor: palette.fg,
    },
  }), [colors, palette]);

  const fallbackIcon = OUTPUT_TYPE_ICON[tool?.outputType] || tool?.icon || 'wand-magic-sparkles';

  return (
    <Pressable
      onPress={() => onOpen?.(run)}
      accessibilityRole='button'
      accessibilityLabel={`Open ${tool?.name || 'tool'} run from ${formatTimeAgo(run?.createdAt)}`}
      style={({ hovered }) => [styles.card, hovered && styles.cardHover]}
    >
      {({ hovered }) => (
        <>
          <View style={[styles.thumb, { aspectRatio: previewAspect }]}>
            {isImage && fileUrl ? (
              <Image source={{ uri: fileUrl }} style={styles.thumbMedia} resizeMode='cover' />
            ) : isVideo && fileUrl && Platform.OS === 'web' ? (
              // eslint-disable-next-line react/forbid-elements
              <video src={fileUrl} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : posterUrl ? (
              <Image source={{ uri: posterUrl }} style={styles.thumbMedia} resizeMode='cover' />
            ) : isAudio ? (
              <>
                <View style={styles.thumbAccent} />
                <Icon name='waveform-lines' size={42} color={palette.fg} />
              </>
            ) : previewText ? (
              <View style={styles.textPreview}>
                <View style={styles.textPreviewMark} />
                <Text size='s' color='secondary' numberOfLines={6} style={{ flex: 1, lineHeight: 19 }}>
                  {previewText}
                </Text>
              </View>
            ) : (
              <>
                <View style={styles.thumbAccent} />
                <Icon name={fallbackIcon} size={42} color={palette.fg} />
              </>
            )}

            {(isImage || isVideo || posterUrl) && (
              <LinearGradient
                colors={['transparent', 'rgba(0, 0, 0, 0.6)']}
                style={styles.scrim}
                pointerEvents='none'
              />
            )}

            <View style={styles.statusChip}>
              <Icon name={status.icon} size={10} color={status.color} solid />
              <Text size='xxs' weight='bold' style={{ color: '#fff' }}>{status.label}</Text>
            </View>

            {!!onDelete && (
              <Pressable
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onDelete(run);
                }}
                disabled={isDeleting}
                style={({ hovered: btnHovered }) => [
                  styles.deleteBtn,
                  btnHovered && styles.deleteBtnHover,
                ]}
                accessibilityLabel={t('pages.tools.deleteRun') || 'Delete run'}
              >
                {isDeleting
                  ? <ActivityIndicator size='small' color='#fff' />
                  : <Icon name='trash' size={11} color='#fff' />}
              </Pressable>
            )}
          </View>

          <View style={styles.body}>
            <View style={styles.toolRow}>
              <ToolAvatar
                tool={tool}
                size={28}
                iconSize={13}
                borderRadius={8}
                backgroundColor={palette.bg}
                color={palette.fg}
                fallbackIcon={fallbackIcon}
                solid
                style={{ flexShrink: 0 }}
              />
              <View style={styles.toolMeta}>
                <Text size='s' color='primary' weight='bold' numberOfLines={1}>
                  {tool?.name || run?.toolSlug}
                </Text>
                <Text size='xs' color='tertiary' numberOfLines={1}>
                  {formatTimeAgo(run?.createdAt)}
                  {!!tool?.category?.name && ` · ${tool.category.name}`}
                </Text>
              </View>
            </View>

            <View style={styles.actionRow}>
              <View style={[styles.actionBtn, hovered && styles.actionBtnHover]}>
                <Icon
                  name='arrow-up-right-from-square'
                  size={10}
                  color={hovered ? '#fff' : palette.fg}
                  solid
                />
                <Text
                  size='xs'
                  weight='bold'
                  style={{ color: hovered ? '#fff' : palette.fg }}
                >
                  {t('pages.tools.openInTool') || 'Open in tool'}
                </Text>
              </View>
              {run?.creditCost != null && Number(run.creditCost) > 0 && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
                  <Icon name='bolt' size={10} color={colors.text_color_tertiary} solid />
                  <Text size='xs' color='tertiary'>{run.creditCost}</Text>
                </View>
              )}
            </View>
          </View>
        </>
      )}
    </Pressable>
  );
}

export default React.memo(ToolHistoryCard);
